import React, { createContext, useContext, useCallback, useMemo } from "react";
import { Modal } from "antd-mobile";
import general from "../../../../../general";
import Dialog from "../../../../../lib/Dialog";
import { useEnvelopeStatuses } from "./useEnvelopeStatuses";
import OpenRedEnvelope from "./OpenRedEnvelope";

const RedEnvelopeContext = createContext(null);

export function RedEnvelopeProvider({ children }) {
  const { envelopeStatuses, updateEnvelopeStatus } = useEnvelopeStatuses();

  const getRedEnvelopeInfo = useCallback((redenvelope_id) => {
    Dialog.loading();
    return new Promise((resolve, reject) => {
      general.axios
        .post("/getRedEnvelopeInfo", { redenvelope_id })
        .finally(() => Dialog.close())
        .then((res) => {
          const { status, data } = res.data;
          if (status.succeed) {
            if (data.is_exists) updateEnvelopeStatus(data, true);
            resolve(data);
          } else {
            Dialog.error(status.error_desc);
            reject(new Error(status.error_desc));
          }
        })
        .catch(reject);
    });
  }, [updateEnvelopeStatus]);

  const handleRecord = useCallback((attach) => {
    Modal.clear();
    setTimeout(() => {
      general.history.push("/redEnvelopeRecord?id=" + attach.redenvelope_id);
    }, 400);
  }, []);

  const handleOpen = useCallback((attach) => {
    Dialog.loading();
    general.axios
      .post("/grabClusterRedEnvelope", { redenvelope_id: attach.redenvelope_id })
      .finally(() => Dialog.close())
      .then((res) => {
        const { status } = res.data;
        if (status.succeed) {
          updateEnvelopeStatus(attach, true);
          handleRecord(attach);
        } else {
          Dialog.info(status.error_desc);
        }
      })
      .catch(() => {});
  }, [updateEnvelopeStatus, handleRecord]);

  const openRedEnvelopeAlert = useCallback((attach) => {
    Modal.show({
      content: (
        <OpenRedEnvelope
          destroyOnClose
          attach={attach}
          handleOpen={(a) => handleOpen(a)}
          handleAction={(a) => {
            Modal.clear();
            setTimeout(() => {
              const task_type = parseInt(a.task_condition.task_type);
              if (task_type === 1) {
                general.history.push("/game?id=" + a.task_condition.game_id);
              } else if (task_type === 2) {
                general.history.push(
                  "/gameCommunity?topic_id=" +
                    a.task_condition.topic_id +
                    "&circles_type=" +
                    a.task_condition.circles_type
                );
              }
            }, 400);
          }}
        />
      ),
      closeOnMaskClick: true,
      destroyOnClose: true,
      getContainer: document.body,
      bodyClassName: "red-envelpoe-modal",
      bodyStyle: { padding: 0, backgroundColor: "transparent" },
    });
  }, [handleOpen]);

  const handleAction = useCallback((attach) => {
    Dialog.loading();
    Modal.clear();
    setTimeout(() => {
      Dialog.close();
      getRedEnvelopeInfo(attach.redenvelope_id)
        .then((data) => {
          if (data.is_pop_bullet) {
            openRedEnvelopeAlert(data);
          } else {
            handleRecord(data);
          }
        })
        .catch(() => {});
    }, 400);
  }, [getRedEnvelopeInfo, openRedEnvelopeAlert, handleRecord]);

  const contextValue = useMemo(() => ({
    envelopeStatuses,
    updateEnvelopeStatus,
    getRedEnvelopeInfo,
    openRedEnvelopeAlert,
    handleRecord,
    handleAction,
  }), [envelopeStatuses, updateEnvelopeStatus, getRedEnvelopeInfo, openRedEnvelopeAlert, handleRecord, handleAction]);

  return (
    <RedEnvelopeContext.Provider value={contextValue}>
      {children}
    </RedEnvelopeContext.Provider>
  );
}

export function useRedEnvelope() {
  const ctx = useContext(RedEnvelopeContext);
  if (!ctx) throw new Error("useRedEnvelope must be used inside RedEnvelopeProvider");
  return ctx;
}
